/**
 * MediaCapture Component
 *
 * Handles recording and uploading audio, video, and image media
 * for survey responses.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import * as mediaApi from '../../api/media'

/**
 * Audio recorder component
 */
function AudioRecorder({ onCapture, onError, maxDurationMs = 300000 }) {
  const [recording, setRecording] = useState(false)
  const [audioUrl, setAudioUrl] = useState(null)
  const [duration, setDuration] = useState(0)
  const mediaRecorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const startTimeRef = useRef(null)
  const timerRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (audioUrl) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])

  const stopRecording = useCallback(() => {
    console.log(
      '[AudioRecorder] stopRecording called, recorder state:',
      mediaRecorderRef.current?.state
    )

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (mediaRecorderRef.current) {
      const state = mediaRecorderRef.current.state
      if (state === 'recording') {
        console.log('[AudioRecorder] Stopping recorder...')
        mediaRecorderRef.current.stop()
      } else {
        console.warn('[AudioRecorder] Recorder not in recording state:', state)
      }
    }

    setRecording(false)
  }, [])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Check what mime types are supported
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg',
        '', // Let browser choose default
      ]

      let selectedMimeType = ''
      for (const type of mimeTypes) {
        if (type === '' || MediaRecorder.isTypeSupported(type)) {
          selectedMimeType = type
          console.log('[AudioRecorder] Using mimeType:', type || '(browser default)')
          break
        }
      }

      const options = selectedMimeType ? { mimeType: selectedMimeType } : {}
      const mediaRecorder = new MediaRecorder(stream, options)

      console.log(
        '[AudioRecorder] MediaRecorder created, state:',
        mediaRecorder.state,
        'mimeType:',
        mediaRecorder.mimeType
      )

      chunksRef.current = []
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (e) => {
        console.log('[AudioRecorder] ondataavailable fired, data size:', e.data.size)
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.onerror = (e) => {
        console.error('[AudioRecorder] MediaRecorder error:', e)
        onError?.('Recording error: ' + (e.error?.message || 'Unknown error'))
      }

      mediaRecorder.onstop = () => {
        console.log('[AudioRecorder] onstop fired, chunks collected:', chunksRef.current.length)
        const totalSize = chunksRef.current.reduce((acc, chunk) => acc + chunk.size, 0)
        console.log('[AudioRecorder] Total data size:', totalSize)

        if (chunksRef.current.length === 0 || totalSize === 0) {
          console.error('[AudioRecorder] No data recorded!')
          onError?.('No audio data was recorded. Please try again.')
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        const actualMimeType = mediaRecorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: actualMimeType })
        console.log('[AudioRecorder] Created blob, size:', blob.size, 'type:', blob.type)
        const url = URL.createObjectURL(blob)
        setAudioUrl(url)
        const recordingDuration = Date.now() - startTimeRef.current
        onCapture(blob, recordingDuration)
        stream.getTracks().forEach((track) => track.stop())
      }

      // Start recording WITHOUT timeslice - data will be available when stop() is called
      mediaRecorder.start()
      console.log('[AudioRecorder] Recording started, state:', mediaRecorder.state)

      startTimeRef.current = Date.now()
      setRecording(true)
      setDuration(0)

      // Update duration display
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current
        setDuration(elapsed)

        // Auto-stop at max duration
        if (elapsed >= maxDurationMs) {
          stopRecording()
        }
      }, 100)
    } catch (err) {
      console.error('[AudioRecorder] Failed to start recording:', err)
      onError?.(err.message || 'Failed to access microphone')
    }
  }, [maxDurationMs, onCapture, onError, stopRecording])

  const clearRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
      setAudioUrl(null)
      setDuration(0)
      onCapture(null, 0)
    }
  }, [audioUrl, onCapture])

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="media-recorder audio-recorder">
      <div className="recorder-controls">
        {!recording && !audioUrl && (
          <button type="button" className="record-btn" onClick={startRecording}>
            <span className="record-icon">🎤</span>
            Start Recording
          </button>
        )}
        {recording && (
          <>
            <button type="button" className="stop-btn recording" onClick={stopRecording}>
              <span className="stop-icon">⏹</span>
              Stop Recording
            </button>
            <span className="duration recording-indicator">
              <span className="pulse"></span>
              {formatDuration(duration)}
            </span>
          </>
        )}
        {audioUrl && !recording && (
          <div className="recording-preview">
            <audio controls src={audioUrl} className="audio-preview" />
            <button type="button" className="clear-btn" onClick={clearRecording}>
              ✕ Re-record
            </button>
          </div>
        )}
      </div>
      {maxDurationMs && <p className="media-hint">Max duration: {formatDuration(maxDurationMs)}</p>}
    </div>
  )
}

/**
 * Video recorder component
 * Uses RecordRTC for reliable cross-browser recording
 */
function VideoRecorder({ onCapture, onError, maxDurationMs = 120000 }) {
  const [recording, setRecording] = useState(false)
  const [videoUrl, setVideoUrl] = useState(null)
  const [duration, setDuration] = useState(0)
  const [streamActive, setStreamActive] = useState(false)
  const videoRef = useRef(null)
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const startTimeRef = useRef(null)
  const timerRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (videoUrl) URL.revokeObjectURL(videoUrl)
      if (recorderRef.current) {
        recorderRef.current.destroy()
        recorderRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [videoUrl])

  const startCamera = useCallback(async () => {
    try {
      console.log('[VideoRecorder] Requesting camera access...')
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      })

      console.log(
        '[VideoRecorder] Got stream with',
        stream.getVideoTracks().length,
        'video,',
        stream.getAudioTracks().length,
        'audio tracks'
      )

      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.muted = true
        videoRef.current.play().catch((e) => console.warn('[VideoRecorder] Video play warning:', e))
      }
      setStreamActive(true)
    } catch (err) {
      console.error('[VideoRecorder] Failed to access camera:', err)
      onError?.(err.message || 'Failed to access camera')
    }
  }, [onError])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStreamActive(false)
  }, [])

  const stopRecording = useCallback(() => {
    console.log('[VideoRecorder] stopRecording called')

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        const blob = recorderRef.current.getBlob()
        console.log('[VideoRecorder] RecordRTC stopped, blob size:', blob.size)

        if (blob.size < 1000) {
          console.error('[VideoRecorder] Recording too small:', blob.size)
          onError?.('No video data recorded. Please try again.')
          stopCamera()
          setRecording(false)
          return
        }

        const url = URL.createObjectURL(blob)
        setVideoUrl(url)
        const recordingDuration = Date.now() - startTimeRef.current
        console.log('[VideoRecorder] Recording duration:', recordingDuration, 'ms')
        onCapture(blob, recordingDuration)
        stopCamera()
        setRecording(false)
      })
    } else {
      setRecording(false)
    }
  }, [onCapture, onError, stopCamera])

  const startRecording = useCallback(async () => {
    if (!streamRef.current) {
      console.error('[VideoRecorder] No stream available')
      onError?.('Camera stream not available')
      return
    }

    try {
      // Dynamically import RecordRTC
      const RecordRTC = (await import('recordrtc')).default

      console.log('[VideoRecorder] Using RecordRTC for reliable recording')

      const recorder = new RecordRTC(streamRef.current, {
        type: 'video',
        mimeType: 'video/webm',
        // Use MediaStreamRecorder for better compatibility
        recorderType: RecordRTC.MediaStreamRecorder,
        // Disable logs in production
        disableLogs: false,
        // Time slice for getting data periodically
        timeSlice: 1000,
        // Callback when data is available
        ondataavailable: (blob) => {
          console.log('[VideoRecorder] RecordRTC data available:', blob.size, 'bytes')
        },
      })

      recorderRef.current = recorder

      recorder.startRecording()
      console.log('[VideoRecorder] RecordRTC recording started')

      startTimeRef.current = Date.now()
      setRecording(true)
      setDuration(0)

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current
        setDuration(elapsed)

        if (elapsed >= maxDurationMs) {
          stopRecording()
        }
      }, 100)
    } catch (err) {
      console.error('[VideoRecorder] Failed to start recording:', err)
      onError?.(err.message || 'Failed to start recording')
    }
  }, [maxDurationMs, onError, stopRecording])

  const clearRecording = useCallback(() => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl)
      setVideoUrl(null)
      setDuration(0)
      onCapture(null, 0)
    }
  }, [videoUrl, onCapture])

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="media-recorder video-recorder">
      <div className="video-container">
        {!videoUrl && (
          <video ref={videoRef} autoPlay muted playsInline className="video-preview live" />
        )}
        {videoUrl && <video controls src={videoUrl} className="video-preview recorded" />}
      </div>
      <div className="recorder-controls">
        {!streamActive && !videoUrl && (
          <button type="button" className="record-btn" onClick={startCamera}>
            <span className="record-icon">📹</span>
            Start Camera
          </button>
        )}
        {streamActive && !recording && (
          <button type="button" className="record-btn" onClick={startRecording}>
            <span className="record-icon">⏺</span>
            Start Recording
          </button>
        )}
        {recording && (
          <>
            <button type="button" className="stop-btn recording" onClick={stopRecording}>
              <span className="stop-icon">⏹</span>
              Stop Recording
            </button>
            <span className="duration recording-indicator">
              <span className="pulse"></span>
              {formatDuration(duration)}
            </span>
          </>
        )}
        {videoUrl && !recording && (
          <button type="button" className="clear-btn" onClick={clearRecording}>
            ✕ Re-record
          </button>
        )}
      </div>
      {maxDurationMs && <p className="media-hint">Max duration: {formatDuration(maxDurationMs)}</p>}
    </div>
  )
}

/**
 * Image capture component
 */
function ImageCapture({ onCapture, onError, allowUpload = true }) {
  const [imageUrl, setImageUrl] = useState(null)
  const [streamActive, setStreamActive] = useState(false)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const fileInputRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [imageUrl])

  // Wire the stream to the video element once it's rendered in the DOM
  useEffect(() => {
    if (streamActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current
    }
  }, [streamActive])

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      streamRef.current = stream
      // Note: the video element may not be in the DOM yet (it's conditional on streamActive),
      // so the useEffect above handles wiring once the element renders.
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      setStreamActive(true)
    } catch (err) {
      console.error('Failed to access camera:', err)
      onError?.(err.message || 'Failed to access camera')
    }
  }, [onError])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStreamActive(false)
  }, [])

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0)

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          setImageUrl(url)
          onCapture(blob, { width: canvas.width, height: canvas.height })
          stopCamera()
        }
      },
      'image/jpeg',
      0.9
    )
  }, [onCapture, stopCamera])

  const handleFileUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        onError?.('Please select an image file')
        return
      }

      const url = URL.createObjectURL(file)
      setImageUrl(url)

      // Get image dimensions
      const img = new Image()
      img.onload = () => {
        onCapture(file, { width: img.width, height: img.height })
      }
      img.src = url
    },
    [onCapture, onError]
  )

  const clearImage = useCallback(() => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl)
      setImageUrl(null)
      onCapture(null, null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [imageUrl, onCapture])

  return (
    <div className="media-recorder image-capture">
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <div className="image-container">
        {!imageUrl && streamActive && (
          <video ref={videoRef} autoPlay muted playsInline className="camera-preview" />
        )}
        {imageUrl && <img src={imageUrl} alt="Captured" className="image-preview" />}
      </div>
      <div className="recorder-controls">
        {!streamActive && !imageUrl && (
          <>
            <button type="button" className="record-btn" onClick={startCamera}>
              <span className="record-icon">📷</span>
              Take Photo
            </button>
            {allowUpload && (
              <>
                <span className="or-divider">or</span>
                <label className="upload-btn">
                  <span>📁 Upload Image</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </>
            )}
          </>
        )}
        {streamActive && !imageUrl && (
          <>
            <button type="button" className="capture-btn" onClick={capturePhoto}>
              <span className="capture-icon">📸</span>
              Capture
            </button>
            <button type="button" className="cancel-btn" onClick={stopCamera}>
              Cancel
            </button>
          </>
        )}
        {imageUrl && (
          <button type="button" className="clear-btn" onClick={clearImage}>
            ✕ Retake / Remove
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Main MediaCapture component
 * Routes to appropriate sub-component based on media type
 */
export default function MediaCapture({
  type,
  questionId,
  surveyId,
  responseId,
  uploadToken,
  value,
  onChange,
  error,
  maxDurationMs,
  allowUpload = true,
}) {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)

  // Handle captured media
  const handleCapture = useCallback(
    async (blob, metadata) => {
      if (!blob) {
        onChange(null)
        return
      }

      console.log('[MediaCapture] handleCapture called with blob:', {
        size: blob.size,
        type: blob.type,
        metadata,
      })

      if (blob.size === 0) {
        console.error('[MediaCapture] Blob is empty!')
        setUploadError('Recording is empty. Please try again.')
        return
      }

      setUploading(true)
      setUploadError(null)
      setUploadProgress(0)

      try {
        // Generate filename
        const filename = mediaApi.generateMediaFilename(questionId, blob.type)
        console.log('[MediaCapture] Generated filename:', filename)

        // Create file from blob
        const file = new File([blob], filename, { type: blob.type })
        console.log('[MediaCapture] Created file:', {
          name: file.name,
          size: file.size,
          type: file.type,
        })

        // Upload the media
        const uploadResult = await mediaApi.uploadMediaAuto(
          surveyId,
          responseId || 'temp',
          questionId,
          file,
          {
            onProgress: setUploadProgress,
            uploadToken,
          }
        )

        // Create the media answer
        const answer = mediaApi.createMediaAnswer(uploadResult, {
          durationMs: typeof metadata === 'number' ? metadata : metadata?.durationMs,
          width: metadata?.width,
          height: metadata?.height,
        })

        onChange(answer)
      } catch (err) {
        console.error('Failed to upload media:', err)
        setUploadError(err.message || 'Failed to upload media')
      } finally {
        setUploading(false)
      }
    },
    [questionId, surveyId, responseId, uploadToken, onChange]
  )

  // Handle errors from sub-components
  const handleError = useCallback((message) => {
    setUploadError(message)
  }, [])

  // Show upload progress
  if (uploading) {
    return (
      <div className="media-capture uploading">
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <span className="progress-text">Uploading... {uploadProgress}%</span>
        </div>
      </div>
    )
  }

  // Show uploaded media preview
  if (value?.media_url) {
    return (
      <div className="media-capture has-value">
        {type === 'audio' && <audio controls src={value.media_url} className="media-preview" />}
        {type === 'video' && <video controls src={value.media_url} className="media-preview" />}
        {type === 'image' && <img src={value.media_url} alt="Uploaded" className="media-preview" />}
        <button type="button" className="clear-btn" onClick={() => onChange(null)}>
          ✕ Remove & Re-capture
        </button>
      </div>
    )
  }

  return (
    <div className={`media-capture ${error ? 'has-error' : ''}`}>
      {type === 'audio' && (
        <AudioRecorder
          onCapture={handleCapture}
          onError={handleError}
          maxDurationMs={maxDurationMs || 300000}
        />
      )}
      {type === 'video' && (
        <VideoRecorder
          onCapture={handleCapture}
          onError={handleError}
          maxDurationMs={maxDurationMs || 120000}
        />
      )}
      {type === 'image' && (
        <ImageCapture onCapture={handleCapture} onError={handleError} allowUpload={allowUpload} />
      )}
      {uploadError && <div className="media-error">{uploadError}</div>}
    </div>
  )
}
